"use client";

import { useState } from "react";
import Image from "next/image";
import { ArrowRight, Check, Copy } from "lucide-react";
import { ROUTES } from "@/lib/routes";

const GRID_LINE_COLOR = "#E0EAF4";
const CURL_COMMAND = `curl -X POST https://api.ecom-listing.ai/public/v1/campaigns \\
  -H "Authorization: Bearer lo_live_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "asin": "B07XJ8C8F5",
    "country": "us",
    "generate_main_images": true,
    "generate_secondary_images": true
  }'`;

export default function PricingCoverSection() {
  const [hasCopied, setHasCopied] = useState(false);
  const copyButtonLabel = hasCopied ? "Copied curl command" : "Copy curl command";

  async function handleCopyCurl() {
    if (!navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(CURL_COMMAND);
    } catch {
      // navigator.clipboard.writeText rejects in non-secure contexts, restricted
      // browsers, and iframes without clipboard-write permission. Leave the button
      // in the default "Copy" state rather than producing an unhandled rejection.
      return;
    }
    setHasCopied(true);
    window.setTimeout(() => setHasCopied(false), 1800);
  }

  return (
    <section className="relative isolate flex min-h-[580px] w-full justify-center overflow-hidden bg-white px-4 pb-6 pt-[88px] md:min-h-[500px] md:px-8 md:pt-[104px]">
      <div className="relative mx-auto flex w-full max-w-[1544px] flex-col items-center text-center">
        <div className="relative z-20 flex w-full max-w-[1040px] flex-col items-center gap-5">
          <h2
            className="max-w-[920px]"
            style={{
              fontFamily: "'Hanken Grotesk', sans-serif",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "clamp(36px, 4.5vw, 52px)",
              lineHeight: "clamp(44px, 5.9vw, 68px)",
              color: "#1B1C30",
            }}
          >
            Build, Automate & Scale Amazon Listings with AI
          </h2>
          <p
            className="max-w-[980px]"
            style={{
              fontFamily: "'Figtree', sans-serif",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "18px",
              lineHeight: "24px",
              color: "#262626",
            }}
          >
            Automate high-converting images and A+ content to build faster and scale your Amazon listings effortlessly.
          </p>
          <div className="mt-1 flex w-full max-w-[220px] flex-col items-center gap-3 sm:flex-row sm:gap-5">
            <a
              href={ROUTES.pricingAnchor}
              className="inline-flex h-14 w-full max-w-[250px] items-center justify-center gap-3 rounded-lg bg-[#1464FF] px-6 text-base font-semibold tracking-[0.2px] text-white transition-colors hover:bg-[#0D4FD6] sm:max-w-none sm:flex-1"
              style={{ fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif" }}
            >
              Get Started
              <ArrowRight aria-hidden="true" size={20} strokeWidth={1.8} />
            </a>
          </div>
        </div>

        <div className="relative mt-[92px] w-full max-w-[704px] pb-24 md:mt-[108px] md:max-w-[1404px] md:pb-18">
          <div className="relative w-full pt-6">
            <div
              data-testid="pricing-cover-grid"
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(${GRID_LINE_COLOR} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE_COLOR} 1px, transparent 1px)`,
                backgroundSize: "100% 20px, 20px 100%",
                opacity: 1,
              }}
            />
            <div className="relative z-20 mx-auto w-full max-w-[820px] overflow-hidden rounded-[22px] bg-[#263149] text-left shadow-[0_28px_80px_rgba(27,28,48,0.24)]">
              <div className="flex min-h-[68px] items-center justify-between border-b border-[#43506A] px-5 sm:px-7">
                <div className="flex items-center gap-3" aria-hidden="true">
                  <span className="h-3 w-3 rounded-full bg-[#E12D2D]" />
                  <span className="h-3 w-3 rounded-full bg-[#F8A516]" />
                  <span className="h-3 w-3 rounded-full bg-[#16C68F]" />
                </div>
                <div className="flex min-w-0 items-center gap-3 font-mono text-sm font-semibold text-[#B7C6E1] sm:text-lg">
                  <span className="shrink-0 text-[#B7C6E1]">POST</span>
                  <span className="truncate text-[#D6E5FF]">/public/v1/campaigns</span>
                </div>
              </div>
              <div className="relative">
                <button
                  type="button"
                  aria-label={copyButtonLabel}
                  onClick={handleCopyCurl}
                  className="absolute right-4 top-4 z-10 inline-flex h-9 items-center gap-2 rounded-md border border-[#53607B] bg-[#303B55] px-3 font-mono text-xs font-semibold text-[#D6E5FF] transition-colors hover:bg-[#3A4663] focus:outline-none focus:ring-2 focus:ring-[#86A8FF] focus:ring-offset-2 focus:ring-offset-[#263149]"
                >
                  {hasCopied ? <Check aria-hidden="true" size={14} /> : <Copy aria-hidden="true" size={14} />}
                  {hasCopied ? "Copied" : "Copy"}
                </button>
                <pre className="whitespace-pre-wrap break-words px-5 pb-6 pt-16 font-mono text-[11px] leading-[1.65] text-[#D6E5FF] sm:px-7 sm:py-6 sm:pr-32 sm:text-[13px] md:text-[14px] lg:text-[15px]">
                  <code>{CURL_COMMAND}</code>
                </pre>
              </div>
            </div>
          </div>
          <Image
            src="/group6/4 copy 2 1.png"
            alt="Game-day football listing creative"
            width={171}
            height={159}
            className="absolute left-[7%] top-[154px] z-10 hidden rotate-[5deg] drop-shadow-[0_24px_24px_rgba(27,28,48,0.22)] sm:block lg:left-[3%]"
          />
          <Image
            src="/group6/1 6.png"
            alt="Dog chew toy listing creative"
            width={156}
            height={133}
            className="absolute right-[2%] top-[16px] z-10 hidden -rotate-[7deg] drop-shadow-[0_22px_22px_rgba(27,28,48,0.22)] md:block"
          />
          <Image
            src="/group6/3B 2.png"
            alt="Face refresh listing creative"
            width={143}
            height={133}
            className="absolute right-[17%] top-[190px] z-30 hidden rotate-[4deg] drop-shadow-[0_22px_22px_rgba(27,28,48,0.22)] min-[1300px]:block"
          />
        </div>
      </div>
    </section>
  );
}
